// @react
import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'
// @common
import { contentTypes, ContentType } from 'constants/contentTypesMap'
// @components
import resultSets, { ResultSet } from '../../config/resultSets'
import colors from '@bit/atd.web.colors'
import Spacer from '@bit/atd.web.spacer'
import Button from '@bit/atd.web.button'
import Icon from '@bit/atd.web.icons'
import Box from '@bit/atd.web.box'
import Grid from '@bit/atd.web.grid'
import Typography from '@material-ui/core/Typography'
import { Divider, useMediaQuery } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import searchRecommendations, {
  SearchOption,
} from '../../config/searchRecommendations'
// @algolia core
import { connectHits, Configure, Index } from 'react-instantsearch-dom'
// @algolia widgets
import Hits from '../Widgets/Hits'
import Results from '../Widgets/Results'
import CustomStats from '../Widgets/Stats'
import { HeaderSearchContext } from 'modules/common/context/headerSearch'
import sortBy from 'modules/search/config/sortBy'
import theme from '@bit/atd.web.theme'
import config from 'constants/config'

const CustomHits = connectHits(Hits)

const useStyles = makeStyles({
  title: {
    fontSize: '32px',
    lineHeight: '44px',
    fontWeight: 600,
    color: colors.black,
    marginBottom: '16px',
  },
  button: {
    marginBottom: '32px',
  },
  pushRight: {
    marginRight: '10px',
  },
  description: {
    margin: '32px 0px 20px',
    fontSize: '18px',
    fontWeight: 500,
  },
})

/**********************************
 *  TYPES
 **********************************/
type PropsType = {
  index: string
  baseIndex: string
  isNoResults: boolean
  setIsNoResults: (arg: boolean) => void
}

const SearchResults = (props: PropsType) => {
  /**********************************
   *  PROPS
   **********************************/
  const { index, baseIndex, isNoResults, setIsNoResults } = props

  /**********************************
   *  STATE
   **********************************/

  const history = useHistory()
  const classes = useStyles()
  const headerSearchContext = useContext(HeaderSearchContext)

  /**********************************
   *  HOOKS
   **********************************/
  const isMobile = useMediaQuery(
    theme.breakpoints.down('sm'),
    config.mediaQuery,
  )

  /**
   *
   */
  const getResultsSets = (): ResultSet[] => {
    if (index === 'master') return resultSets
    const content = contentTypes.find(
      (content: ContentType) => content.index === baseIndex,
    )
    if (!content) history.push('/search')

    return [
      {
        index: index,
        title: content?.pluralLabel || '',
        hitsPerPage: 12,
      },
    ]
  }

  const defaultRecommendSet = [
    {
      index: 'courses',
      title: 'Courses',
    },
  ]

  const recommendSets =
    searchRecommendations.find(
      (recommendation) => recommendation.index === index,
    )?.searchOptions || defaultRecommendSet

  /**
   *
   * @param contextValue
   * @param allSearchResults
   */
  const onSearchResults = ({ contextValue, allSearchResults }: any) => {
    const searchStore = contextValue.store.getState()
    if (allSearchResults && !searchStore.searching) {
      const searchResults = Object.keys(allSearchResults).reduce(
        (acc: any[], key) =>
          key.startsWith('custom') ? acc : [...acc, allSearchResults[key]],
        [],
      )
      if (index === 'master') {
        const isNoResultValue = !Object.values(searchResults).some(
          (result: any) => result.nbHits,
        )
        setIsNoResults(isNoResultValue)
      } else {
        const sortByIndexes = sortBy.find(
          (sortBy) => sortBy.index === baseIndex,
        )
        const isNoResultValue = allSearchResults.hasOwnProperty('nbHits')
          ? !allSearchResults.nbHits
          : !sortByIndexes?.values.some(
              ({ value }: any) => allSearchResults[value]?.nbHits,
            )
        setIsNoResults(isNoResultValue)
      }
    }
  }

  /**********************************
   *  RENDER
   **********************************/
  return (
    <>
      {getResultsSets().map((item: ResultSet, id, results) => {
        const actions =
          index === 'master' ? (
            <Button
              variant="tertiary"
              href={`/search/${item.index}/${history.location.search}`}
            >
              <Spacer size={8} />
              <span className={classes.pushRight}>View all results</span>
              <Icon glyph="arrow-right" size={16} color={colors.ctaPrimary} />
            </Button>
          ) : (
            <></>
          )

        return (
          <Grid key={item.index} item xs={12}>
            <Index
              indexName={item.index}
              // @ts-ignore
              title={item.title}
            >
              <Configure hitsPerPage={item.hitsPerPage} />
              <Results
                onSearchResults={results.length - 1 === id && onSearchResults}
              >
                <Box
                  header={{
                    title: (
                      <CustomStats
                        resultName={'result'}
                        title={` in ${item.title}`}
                      />
                    ),
                    actionsAlignment: 'right',
                    actions: isMobile ? null : actions,
                  }}
                  footer={{
                    alignment: 'right',
                    content: isMobile ? actions : '',
                  }}
                  variant={'transparent'}
                >
                  <CustomHits />
                </Box>
                <Spacer size={40} />
              </Results>
            </Index>
          </Grid>
        )
      })}
      {isNoResults && (
        <Grid item xs={12}>
          <div>
            <Typography variant="h4" className={classes.title}>
              0 search results available.
            </Typography>
            <Button
              variant="primary"
              className={classes.button}
              onClick={() => headerSearchContext.setIsShowSearch(true)}
            >
              <span>Start New Search</span>
            </Button>
            <Divider />
            <div className={classes.description}>
              You might be interested in the following recommendations:
            </div>
          </div>
          {recommendSets.map(
            ({
              index: recommendedIndex,
              query = '',
              hitsPerPage = 12,
              title,
            }: SearchOption) => {
              const actions =
                index === 'master' ? (
                  <Button
                    variant="tertiary"
                    href={`/search/${recommendedIndex}`}
                  >
                    <Spacer size={8} />
                    <span>View all {title}</span>
                    <Icon
                      glyph="arrow-right"
                      size={16}
                      color={colors.ctaPrimary}
                    />
                  </Button>
                ) : (
                  <></>
                )

              return (
                <Index
                  indexName={recommendedIndex}
                  indexId={`custom_${recommendedIndex}`}
                  key={recommendedIndex}
                >
                  <Configure hitsPerPage={hitsPerPage} query={query} />
                  <Results>
                    <Box header={{ title, actions }} variant={'transparent'}>
                      <CustomHits />
                    </Box>
                  </Results>
                </Index>
              )
            },
          )}
        </Grid>
      )}
    </>
  )
}

export default SearchResults
